/**
 * Lightweight embedded analytics tracker.
 *
 * Groups events into sessions which expire after 30 minutes of inactivity
 * and ships them to POST /api/analytics/track using non-blocking
 * navigator.sendBeacon() (fetch with keepalive as a fallback).
 *
 * The session id is kept in localStorage, so the same session is shared
 * by all tabs of this browser where this site is open.
 *
 * URLs are sent in a compact form: page_url is the path only and same-site
 * referrers are reduced to the path as well (the server records the host as
 * a site id), while external referrers keep their host so that the traffic
 * sources remain known.
 *
 * Usage: gpAnalytics.trackEvent('button_click', {button: 'download'});
 */

const SESSION_KEY = 'analytics_session_id';
const LAST_ACTIVE_KEY = 'analytics_last_active';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 1,800,000 ms = 30 minutes
const TRACK_URL = '/api/analytics/track';

export type TrackEventMetadata = { readonly [propName: string]: unknown };

function generateUuidV4(): string {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    // RFC 4122 version 4 fallback for older browsers.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Returns the id of the current session, starting a new one when the
 * previous session has expired after 30 minutes of inactivity.
 * Both the session id and the last-active timestamp live in
 * localStorage, hence the session continues in every tab of this
 * browser and activity in any tab keeps it alive.
 * Refreshes the last-active timestamp on every call.
 */
export function getSessionId(): string {
    const now = Date.now();
    let sessionId: string | null = null;
    let lastActive = 0;
    try {
        sessionId = window.localStorage.getItem(SESSION_KEY);
        lastActive = parseInt(window.localStorage.getItem(LAST_ACTIVE_KEY) || '0', 10) || 0;
    } catch (e) {
        // Storage may be unavailable (e.g. some private browsing modes).
    }

    if (!sessionId || now - lastActive >= SESSION_TIMEOUT_MS) {
        sessionId = generateUuidV4();
        try {
            window.localStorage.setItem(SESSION_KEY, sessionId);
        } catch (e) { /* ignore */ }
    }
    try {
        window.localStorage.setItem(LAST_ACTIVE_KEY, String(now));
    } catch (e) { /* ignore */ }
    return sessionId;
}

/**
 * Reduces the document referrer to what is worth storing. Same-site
 * referrers are reduced to the path only, because the host is recorded
 * server-side as a site id. External referrers keep their host, so that
 * the traffic source remains known. The scheme, query and fragment are
 * always dropped. Returns null when there is no referrer or it cannot
 * be parsed.
 */
function referrerForStorage(referrer: string): string | null {
    if (!referrer) {
        return null;
    }
    try {
        const url = new URL(referrer);
        if (url.host === window.location.host) {
            return url.pathname || '/';
        }
        return url.host + url.pathname;
    } catch (e) {
        return null;
    }
}

/**
 * Captures and dispatches an analytics event. Never blocks the page:
 * uses sendBeacon so that events survive page unloads, and falls back
 * to a fire-and-forget fetch with keepalive.
 */
export function trackEvent(eventName: string, metadata?: TrackEventMetadata): void {
    if (!eventName) {
        return;
    }
    const payload = {
        session_id: getSessionId(),
        event_name: eventName,
        page_url: window.location.pathname || '/',
        referrer: referrerForStorage(document.referrer),
        metadata: metadata || {}
    };
    const body = JSON.stringify(payload);

    if (typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], {type: 'application/json'});
        if (navigator.sendBeacon(TRACK_URL, blob)) {
            return;
        }
        // Beacon queue was full: fall through to fetch.
    }
    try {
        window.fetch(TRACK_URL, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: body,
            keepalive: true,
            credentials: 'omit'
        }).catch(function () { /* fire and forget */ });
    } catch (e) { /* ignore */ }
}

/**
 * Maps a link href to the name of the call-to-action it represents, or null
 * when the link is not a tracked call-to-action.
 */
function buttonName(href: string): string | null {
    if (href.startsWith('/download')) {
        return 'download';
    }
    if (href.indexOf('youtube.com') !== -1) {
        return 'video_tutorial';
    }
    if (href.indexOf('ganttproject.cloud') !== -1) {
        return 'gp_cloud';
    }
    return null;
}

/**
 * Delegated click listener which reports clicks on the landing page
 * call-to-action links (download buttons, video tutorial, GanttProject Cloud)
 * to the embedded analytics tracker. Delegation also covers the links which
 * are injected into the page dynamically (e.g. the ads sidebar content).
 */
function trackLinkClick(event: MouseEvent) {
    const target = event.target as Element | null;
    const link = target && typeof target.closest === 'function'
        ? target.closest('a[href]')
        : null;
    if (!link) {
        return;
    }
    const eventName = link.getAttribute('data-track-event');
    if (eventName) {
        eventName.split(",").forEach((e) => trackEvent(e));
        return;
    }
    const href = link.getAttribute('href') || '';
    const button = buttonName(href);
    if (button) {
        trackEvent('button_click', {button: button, href: href});
    }
}

export function installGanalytics() {
    document.addEventListener('click', trackLinkClick);
    trackEvent('page_view');
}

export function destroyGanalytics() {
    document.removeEventListener('click', trackLinkClick);
}

declare global {
    interface Window {
        gpAnalytics: {
            trackEvent: typeof trackEvent;
            getSessionId: typeof getSessionId;
        };
        trackEvent: typeof trackEvent;
    }
}

window.gpAnalytics = {
    trackEvent: trackEvent,
    getSessionId: getSessionId
};

if (typeof window.trackEvent !== 'function') {
    window.trackEvent = trackEvent;
}
